import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowRight, Target, CheckCircle } from 'lucide-react';
import { getSiteBySlug } from '../utils/siteApi';
import { getFileUrl } from '../utils/fileUpload';
import { apiCall } from '../utils/api';
import { PUBLIC_ACCESS_CODE_VALIDATE, PUBLIC_SURVEYS, S3_BASE_URL } from '../config/api';

const Welcome = () => {
  const location = useLocation();
  const { siteSlug, accessCode: urlAccessCode } = useParams();
  
  // Site data state
  const [siteData, setSiteData] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const fetchingRef = useRef(false);
  const lastFetchedSlugRef = useRef(null);
  
  // Determine actual site slug - use siteSlug from URL params
  const actualSiteSlug = siteSlug;
  
  // MSQ Form state
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    confirmEmail: '',
    accessCode: urlAccessCode || ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [accessCodeValid, setAccessCodeValid] = useState(false);
  const [accessCodeVerifying, setAccessCodeVerifying] = useState(false);
  const [accessCodeData, setAccessCodeData] = useState(null);
  const [session, setSession] = useState(null);

  
  // Check if urlAccessCode is an access code or session
  const checkUrlAccessCodeType = async (urlAccessCode, siteId) => {
    if (!urlAccessCode || !siteId) {
      return { type: null, data: null };
    }

    try {
      // First, try to validate as access code
      const endpoint = PUBLIC_ACCESS_CODE_VALIDATE.replace('{access_code}', urlAccessCode);
      const response = await apiCall(endpoint, {
        method: 'GET',
        params: { site_id: siteId }
      });
      
      if (response && response.data) {

        
        
        const isValid = response.data.valid || response.data.is_valid || false;
        const accessCodeData = response.data.access_code || response.data;
        
        if (isValid && (siteId === response.data.data?.access_code?.site_id || siteId === accessCodeData?.site_id)) {
          console.log('✅ URL parameter is an access code:', accessCodeData);

          
          return { type: 'access_code', data: accessCodeData };
        } 

        if (isValid && (siteId === response.data.data.site_id && ("survey" === response.data.type))) {
          console.log('✅ URL parameter is a survey:', response.data.data );

          
          const surveyData = response.data.data;

          // Restore session from surveyData if available, otherwise generate a new one
          const restoredSession = surveyData.session || Math.random().toString(36).substring(2, 90);
          setSession(restoredSession);
          
          
          
          // Set firstname, lastname, email, and confirm email from survey data
          setFormData(prev => {
            const emailValue = surveyData.email || prev.email || '';
            return {
              ...prev,
              fname: surveyData.fname || surveyData.first_name || prev.fname || '',
              lname: surveyData.lname || surveyData.last_name || prev.lname || '',
              email: emailValue,
              confirmEmail: emailValue,
              accessCode: surveyData.access_code
            };
          });


          // Set Step1 for Position from surveyData
          if (surveyData.PositionPercent) {

            const step1Data = {
              Position1: surveyData.Position1 || '',
              Position2: surveyData.Position2 || '',
              Position3: surveyData.Position3 || '',
              Position4: surveyData.Position4 || '',
              Position5: surveyData.Position5 || ''
            };

            const step2Data = {
              Capability1: surveyData.Capability1 || '',
              Capability2: surveyData.Capability2 || '',
              Capability3: surveyData.Capability3 || '',
              Capability4: surveyData.Capability4 || '',
              Capability5: surveyData.Capability5 || ''
            };

            const step3Data = {
              Channel1: surveyData.Channel1 || '',
              Channel2: surveyData.Channel2 || '',
              Channel3: surveyData.Channel3 || '',
              Channel4: surveyData.Channel4 || '',
              Channel5: surveyData.Channel5 || ''
            };

            const step4Data = {
              Buyer1: surveyData.Buyer1 || '',
              Buyer2: surveyData.Buyer2 || '',
              Buyer3: surveyData.Buyer3 || '',
              Buyer4: surveyData.Buyer4 || '',
              Buyer5: surveyData.Buyer5 || ''
            };

            const step5Data = {
              Communication1: surveyData.Communication1 || '',
              Communication2: surveyData.Communication2 || '',
              Communication3: surveyData.Communication3 || '',
              Communication4: surveyData.Communication4 || '',
              Communication5: surveyData.Communication5 || ''
            };

            const step6Data = {
              Conversations1: surveyData.Conversations1 || '',
              Conversations2: surveyData.Conversations2 || '',
              Conversations3: surveyData.Conversations3 || '',
              Conversations4: surveyData.Conversations4 || '',
              Conversations5: surveyData.Conversations5 || ''
            };

            const step7Data = {
              Product1: surveyData.Product1 || '',
              Product2: surveyData.Product2 || '',
              Product3: surveyData.Product3 || '',
              Product4: surveyData.Product4 || '',
              Product5: surveyData.Product5 || ''
            };

            const step8Data = {
              Management1: surveyData.Management1 || '',
              Management2: surveyData.Management2 || '',
              Management3: surveyData.Management3 || '',
              Management4: surveyData.Management4 || '',
              Management5: surveyData.Management5 || ''
            };

            const step9Data = {
              Service1: surveyData.Service1 || '',
              Service2: surveyData.Service2 || '',
              Service3: surveyData.Service3 || '',
              Service4: surveyData.Service4 || '',
              Service5: surveyData.Service5 || ''
            };

            

            // Get existing survey data from localStorage
            const existingData = localStorage.getItem('survey_form_data');
            const allData = existingData ? JSON.parse(existingData) : {};
            
            // Merge step1 data with existing data
            const updatedData = {
              ...allData,
              step1: step1Data,
              step2: step2Data,
              step3: step3Data,
              step4: step4Data,
              step5: step5Data,
              step6: step6Data,
              step7: step7Data,
              step8: step8Data,
              step9: step9Data
            };
            
            
            localStorage.setItem('survey_form_data', JSON.stringify(updatedData));
            console.log('💾 Step1 Position data saved to localStorage:', step1Data);
          }

          return { type: 'survey', data: surveyData };
        }
      }
      
      // If not an access code, check if it's a session (typically longer alphanumeric string)
      // Sessions are usually longer and more random-looking
      if (urlAccessCode.length > 20) {
        console.log('✅ URL parameter appears to be a session:', urlAccessCode);
        return { type: 'session', data: urlAccessCode };
      }
      
      console.log('❌ URL parameter is neither valid access code nor session');
      return { type: null, data: null };
    } catch (error) {
      console.error('❌ Error checking URL access code type:', error);
      // If validation fails, assume it might be a session if it's long enough
      if (urlAccessCode.length > 20) {
        return { type: 'session', data: urlAccessCode };
      }
      return { type: null, data: null };
    }
  };

  // Access code verification function
  const verifyAccessCode = async (accessCode, siteId) => {
    try {

      const endpoint = PUBLIC_ACCESS_CODE_VALIDATE.replace('{access_code}', accessCode);
      //console.log('🔗 Making API call to:', endpoint);
      
      const response = await apiCall(endpoint, {
        method: 'GET',
        params: { site_id: siteId }
      });
      
      if (response && response.data) {

        const isValid = response.data.valid || response.data.is_valid || false;
        const accessCodeData = response.data.access_code || response.data;
        
        if (isValid && (siteId === response.data.data.access_code.site_id && ("access_code" === response.data.type))) {
          console.log('✅ Access code verified:', accessCodeData);
          return { valid: true, accessCode: accessCodeData };
        }

      }
      
      //console.log('❌ Access code not found or invalid');
      return { valid: false };
    } catch (error) {
      //console.error('❌ Error verifying access code:', error);
      return { valid: false };
    }
  };
  
  // Check URL access code type and update form data when URL access code changes
  useEffect(() => {
    if (urlAccessCode && siteData?.site_id) {
      checkUrlAccessCodeType(urlAccessCode, siteData.site_id).then(result => {
        console.log(result);
        if (result.type === 'access_code') {
          
          // It's an access code, populate the form
          setFormData(prev => ({
            ...prev,
            accessCode: urlAccessCode
          }));
          setAccessCodeValid(true);
          setAccessCodeData(result.data);
          console.log('✅ Access code detected from URL, form auto-populated');
        } else if (result.type === 'survey') {

          
          // It's a survey, populate form with survey data
          console.log('✅ Survey detected from URL:', result.data);
          const surveyData = result.data;
          
          // Restore session from surveyData if available, otherwise generate a new one
          const restoredSession = surveyData.session || Math.random().toString(36).substring(2, 90);
          setSession(restoredSession);
          console.log('✅ Session restored:', restoredSession);
          
          // Set Step1 for Position from surveyData
          if (surveyData.step1) {
            const step1Data = {
              Position1: surveyData.step1.Position1 || '',
              Position2: surveyData.step1.Position2 || '',
              Position3: surveyData.step1.Position3 || '',
              Position4: surveyData.step1.Position4 || '',
              Position5: surveyData.step1.Position5 || ''
            };
            
            // Get existing survey data from localStorage
            const existingData = localStorage.getItem('survey_form_data');
            const allData = existingData ? JSON.parse(existingData) : {};
            
            // Merge step1 data with existing data
            const updatedData = {
              ...allData,
              step1: step1Data
            };
            

            //localStorage.setItem('survey_form_data', JSON.stringify(updatedData));
            console.log('💾 Step1 Position data saved to localStorage:', step1Data);
          }
          
          setFormData(prev => {
            const emailValue = surveyData.email || prev.email || '';
            return {
              ...prev,
              fname: surveyData.fname || surveyData.first_name || prev.fname || '',
              lname: surveyData.lname || surveyData.last_name || prev.lname || '',
              email: emailValue,
              confirmEmail: emailValue,
              accessCode: surveyData.access_code
            };
          });


          
        } else if (result.type === 'session') {
          // It's a session, handle accordingly (maybe redirect or show different UI)
          console.log('✅ Session detected from URL:', result.data);
          
          // You can add session-specific handling here
        } else {
          // Unknown type, still populate as access code for user to verify
          setFormData(prev => ({
            ...prev,
            accessCode: urlAccessCode
          }));
        }
      });
    } else if (urlAccessCode) {
      // If site data not loaded yet, just populate the form
      setFormData(prev => ({
        ...prev,
        accessCode: urlAccessCode
      }));
    }
  }, [urlAccessCode, siteData?.site_id]);

  // Fetch site data based on siteSlug from URL params
  useEffect(() => {
    // Clear localStorage when Welcome page loads
    //localStorage.clear();
    console.log('🧹 localStorage cleared on Welcome page load');
    
    // Use actualSiteSlug for fetching
    const slugToFetch = actualSiteSlug;

    console.log("slugToFetch",slugToFetch);

    // Redirect to MSQ Index homepage if slugToFetch is empty
    if (!slugToFetch) {
      window.location.href = 'https://www.msqindex.com/';
      return;
    }
    
    // Prevent duplicate calls for the same slug
    if (slugToFetch && (fetchingRef.current || lastFetchedSlugRef.current === slugToFetch)) {
      console.log('⏸️ Already fetching or already fetched for this slug, skipping duplicate call');
      return;
    } else {

    }
    
    if (slugToFetch) {
      console.log('🔍 Detected site slug from URL params:', slugToFetch);
      
      // Mark as fetching and store the slug
      fetchingRef.current = true;
      lastFetchedSlugRef.current = slugToFetch;
      
      // Immediately fetch site data based on slug
      const fetchSiteData = async () => {
        setSiteLoading(true);
        try {
          console.log('🚀 Fetching site data for slug:', slugToFetch);
          const response = await getSiteBySlug(slugToFetch);
          
          if (response && response.data) {
            setSiteData(response.data);
            console.log('✅ Site data loaded:', response.data);
            console.log('🖼️ Logo in site data:', response.data.logo);
            console.log('🏷️ Site name in site data:', response.data.site_name);
          } else {
            window.location.href = 'https://www.msqindex.com/';
            console.log('❌ No site found for slug:', slugToFetch);
            setSiteData(null);
          }
        } catch (error) {
          console.error('❌ Error fetching site data:', error);
          setSiteData(null);
        } finally {
          setSiteLoading(false);
          fetchingRef.current = false;
        }
      };

      fetchSiteData();
    } else {
      // No slug in URL, reset site data
      console.log('🔄 No site slug in URL, resetting site data');
      setSiteData(null);
      setSiteLoading(false);
      lastFetchedSlugRef.current = null;
    }

    // Cleanup function to reset fetching flag if component unmounts
    return () => {
      fetchingRef.current = false;
    };
  }, [actualSiteSlug]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear validation states when user types
    if (field === 'accessCode') {
      setAccessCodeValid(false);
      setAccessCodeVerifying(false);
    }
    
    // Clear submit error when user makes any changes
    if (errors.submit) {
      setErrors(prev => ({
        ...prev,
        submit: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fname.trim()) {
      newErrors.fname = 'First name is required';
    }

    if (!formData.lname.trim()) {
      newErrors.lname = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email address';
    } else if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match';
    }

    if (!formData.accessCode.trim()) {
      newErrors.accessCode = 'Access code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMSQSubmit = async (e) => {
    e.preventDefault();
    
    // Basic form validation first
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      
      // Verify access code when submit button is clicked
      if (siteData?.site_id) {
        setAccessCodeVerifying(true);
        const verification = await verifyAccessCode(formData.accessCode.trim(), siteData.site_id);
        setAccessCodeVerifying(false);
        console.log('verification', verification);
       
        if (!verification.valid) {
          
          setAccessCodeValid(false);
          setAccessCodeData(null);
          setErrors(prev => ({
            ...prev,
            accessCode: 'Invalid access code'
          }));
          setIsLoading(false);
          return;
        } else {
          
          setAccessCodeValid(true);
          setAccessCodeData(verification.accessCode);
        }
      }
      
      //console.log('Form submitted:', formData);
      //console.log('Site data:', siteData);
      
      // Use stored session if available, otherwise generate a new one
      const currentSession = session || Math.random().toString(36).substring(2, 90);
      if (!session) {
        setSession(currentSession);
      }
      
      // Extract parent_id and child_id from access code data
      const parent_id = accessCodeData?.parent_id || null;
      const child_id = accessCodeData?.id || null;
      
      // Submit form data to /api/public/surveys and wait for completion
      const surveyData = {
        fname: formData.fname,
        lname: formData.lname,
        email: formData.email,
        access_code: formData.accessCode,
        site_id: siteData?.site_id,
        session: currentSession,
        parent_id: parent_id,
        child_id: child_id
      };
      
      // Wait for API call to complete first
      const response = await apiCall(PUBLIC_SURVEYS, {
        method: 'POST',
        data: surveyData
      });

      console.log('✅ Survey data submitted successfully:', response);

      // Only process localStorage after API call completes successfully
      if (response && response.data && response.data.data) {
        const responseSurveyData = response.data.data;
        console.log('✅ Response survey data:', responseSurveyData);
        
        // Process step data from response
        // Convert to strings and handle 0 as a valid value
        const step1Data = {
          Position1: responseSurveyData.Position1 !== null && responseSurveyData.Position1 !== undefined ? String(responseSurveyData.Position1) : '',
          Position2: responseSurveyData.Position2 !== null && responseSurveyData.Position2 !== undefined ? String(responseSurveyData.Position2) : '',
          Position3: responseSurveyData.Position3 !== null && responseSurveyData.Position3 !== undefined ? String(responseSurveyData.Position3) : '',
          Position4: responseSurveyData.Position4 !== null && responseSurveyData.Position4 !== undefined ? String(responseSurveyData.Position4) : '',
          Position5: responseSurveyData.Position5 !== null && responseSurveyData.Position5 !== undefined ? String(responseSurveyData.Position5) : ''
        };

        const step2Data = {
          Capability1: responseSurveyData.Capability1 !== null && responseSurveyData.Capability1 !== undefined ? String(responseSurveyData.Capability1) : '',
          Capability2: responseSurveyData.Capability2 !== null && responseSurveyData.Capability2 !== undefined ? String(responseSurveyData.Capability2) : '',
          Capability3: responseSurveyData.Capability3 !== null && responseSurveyData.Capability3 !== undefined ? String(responseSurveyData.Capability3) : '',
          Capability4: responseSurveyData.Capability4 !== null && responseSurveyData.Capability4 !== undefined ? String(responseSurveyData.Capability4) : '',
          Capability5: responseSurveyData.Capability5 !== null && responseSurveyData.Capability5 !== undefined ? String(responseSurveyData.Capability5) : ''
        };

        const step3Data = {
          Channel1: responseSurveyData.Channel1 !== null && responseSurveyData.Channel1 !== undefined ? String(responseSurveyData.Channel1) : '',
          Channel2: responseSurveyData.Channel2 !== null && responseSurveyData.Channel2 !== undefined ? String(responseSurveyData.Channel2) : '',
          Channel3: responseSurveyData.Channel3 !== null && responseSurveyData.Channel3 !== undefined ? String(responseSurveyData.Channel3) : '',
          Channel4: responseSurveyData.Channel4 !== null && responseSurveyData.Channel4 !== undefined ? String(responseSurveyData.Channel4) : '',
          Channel5: responseSurveyData.Channel5 !== null && responseSurveyData.Channel5 !== undefined ? String(responseSurveyData.Channel5) : ''
        };
        
        const step4Data = {
          Buyer1: responseSurveyData.Buyer1 !== null && responseSurveyData.Buyer1 !== undefined ? String(responseSurveyData.Buyer1) : '',
          Buyer2: responseSurveyData.Buyer2 !== null && responseSurveyData.Buyer2 !== undefined ? String(responseSurveyData.Buyer2) : '',
          Buyer3: responseSurveyData.Buyer3 !== null && responseSurveyData.Buyer3 !== undefined ? String(responseSurveyData.Buyer3) : '',
          Buyer4: responseSurveyData.Buyer4 !== null && responseSurveyData.Buyer4 !== undefined ? String(responseSurveyData.Buyer4) : '',
          Buyer5: responseSurveyData.Buyer5 !== null && responseSurveyData.Buyer5 !== undefined ? String(responseSurveyData.Buyer5) : ''
        };

        const step5Data = {
          Communication1: responseSurveyData.Communication1 !== null && responseSurveyData.Communication1 !== undefined ? String(responseSurveyData.Communication1) : '',
          Communication2: responseSurveyData.Communication2 !== null && responseSurveyData.Communication2 !== undefined ? String(responseSurveyData.Communication2) : '',
          Communication3: responseSurveyData.Communication3 !== null && responseSurveyData.Communication3 !== undefined ? String(responseSurveyData.Communication3) : '',
          Communication4: responseSurveyData.Communication4 !== null && responseSurveyData.Communication4 !== undefined ? String(responseSurveyData.Communication4) : '',
          Communication5: responseSurveyData.Communication5 !== null && responseSurveyData.Communication5 !== undefined ? String(responseSurveyData.Communication5) : ''
        };

        const step6Data = {
          Conversations1: responseSurveyData.Conversations1 !== null && responseSurveyData.Conversations1 !== undefined ? String(responseSurveyData.Conversations1) : '',
          Conversations2: responseSurveyData.Conversations2 !== null && responseSurveyData.Conversations2 !== undefined ? String(responseSurveyData.Conversations2) : '',
          Conversations3: responseSurveyData.Conversations3 !== null && responseSurveyData.Conversations3 !== undefined ? String(responseSurveyData.Conversations3) : '',
          Conversations4: responseSurveyData.Conversations4 !== null && responseSurveyData.Conversations4 !== undefined ? String(responseSurveyData.Conversations4) : '',
          Conversations5: responseSurveyData.Conversations5 !== null && responseSurveyData.Conversations5 !== undefined ? String(responseSurveyData.Conversations5) : ''
        };

        const step7Data = {
          Product1: responseSurveyData.Product1 !== null && responseSurveyData.Product1 !== undefined ? String(responseSurveyData.Product1) : '',
          Product2: responseSurveyData.Product2 !== null && responseSurveyData.Product2 !== undefined ? String(responseSurveyData.Product2) : '',
          Product3: responseSurveyData.Product3 !== null && responseSurveyData.Product3 !== undefined ? String(responseSurveyData.Product3) : '',
          Product4: responseSurveyData.Product4 !== null && responseSurveyData.Product4 !== undefined ? String(responseSurveyData.Product4) : '',
          Product5: responseSurveyData.Product5 !== null && responseSurveyData.Product5 !== undefined ? String(responseSurveyData.Product5) : ''
        };


        const step8Data = {
          Management1: responseSurveyData.Management1 !== null && responseSurveyData.Management1 !== undefined ? String(responseSurveyData.Management1) : '',
          Management2: responseSurveyData.Management2 !== null && responseSurveyData.Management2 !== undefined ? String(responseSurveyData.Management2) : '',
          Management3: responseSurveyData.Management3 !== null && responseSurveyData.Management3 !== undefined ? String(responseSurveyData.Management3) : '',
          Management4: responseSurveyData.Management4 !== null && responseSurveyData.Management4 !== undefined ? String(responseSurveyData.Management4) : '',
          Management5: responseSurveyData.Management5 !== null && responseSurveyData.Management5 !== undefined ? String(responseSurveyData.Management5) : ''
        };
        
        const step9Data = {
          Service1: responseSurveyData.Service1 !== null && responseSurveyData.Service1 !== undefined ? String(responseSurveyData.Service1) : '',
          Service2: responseSurveyData.Service2 !== null && responseSurveyData.Service2 !== undefined ? String(responseSurveyData.Service2) : '',
          Service3: responseSurveyData.Service3 !== null && responseSurveyData.Service3 !== undefined ? String(responseSurveyData.Service3) : '',
          Service4: responseSurveyData.Service4 !== null && responseSurveyData.Service4 !== undefined ? String(responseSurveyData.Service4) : '',
          Service5: responseSurveyData.Service5 !== null && responseSurveyData.Service5 !== undefined ? String(responseSurveyData.Service5) : ''
        };
        
        // Save all form details to localStorage after API call completes
        const formStorageData = {
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          access_code: formData.accessCode,
          site_id: siteData?.site_id,
          session: currentSession,
          parent_id: parent_id,
          child_id: child_id,
          site_slug: actualSiteSlug,
          submitted_at: new Date().toISOString(),
          step1: step1Data,
          step2: step2Data,
          step3: step3Data,
          step4: step4Data,
          step5: step5Data,
          step6: step6Data,
          step7: step7Data,
          step8: step8Data,
          step9: step9Data
        };
        
        // Store form data in localStorage
        localStorage.setItem('survey_form_data', JSON.stringify(formStorageData));
        console.log('💾 Form data saved to localStorage:', formStorageData);
      } else {
        console.warn('⚠️ API response does not contain expected data structure');
      }
      
      // Clear loading state
      setIsLoading(false);
      
      // Move to next step (MSQ Index)
      console.log('🔄 Moving to next step...');
      //window.location.href = `/msq-index?step=2&fname=${encodeURIComponent(formData.fname)}&lname=${encodeURIComponent(formData.lname)}&email=${encodeURIComponent(formData.email)}`;
      window.location.href = `/msq-index`;
    } catch (error) {
      console.error('Error during form submission:', error);
      setAccessCodeVerifying(false);
      setIsLoading(false);
      
      // Show error message to user
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to submit survey. Please try again.'
      }));
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br bg-white">

      {/* MSQ Index Form Section */}
      <section id="msq-form" className=" bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="">
          
          
          <div className="w-full">
            <div className="bg-white p-16">
              <div className="flex items-start justify-between mb-6">
                <div className="text-left flex-1 pr-6">
                  <h3 className="text-black-500 mb-2" style={{ fontSize: '32px', fontWeight: 600 }}>Helping you transform your marketing from a question mark into a<br />definitive success factor for your businesses.</h3>
                  <p className="text-black-400 pt-0" style={{fontWeight: '400'}}>
                  <strong>Take control of your marketing decisions through educational tools and a structured methodology<br/>that breeds confidence and clarity.  </strong>
                  </p>
                  <p className="text-gray-600 pt-2">
                  We invite you to set aside 10 minutes of uninterrupted time to reflect and complete this survey with<br/>intention. Consider placing your devices on silent and finding a quiet spot free from distractions.
                  </p>
                </div>
                
                {/* Logo on the right side */}
                <div className="flex-shrink-0">
                  {siteData?.logo ? (
                    <div className="w-40 h-40 p-2 flex items-center justify-center">
                      <img
                        src={S3_BASE_URL + '/' + siteData.logo} 
                        alt={`${siteData.site_name || 'Site'} Logo`}
                        className="w-full h-full object-contain"
                        onLoad={() => {
                          console.log('✅ Site logo loaded successfully:', getFileUrl(siteData.logo));
                        }}
                        onError={(e) => {
                          console.log('❌ Error loading site logo:', getFileUrl(siteData.logo));
                          console.log('🔄 Falling back to default logo');
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-3xl" style={{display: 'none'}}>
                        {siteData.site_name ? siteData.site_name.charAt(0).toUpperCase() : 'M'}
          </div>
        </div>
                  ) : (
                    <div className="w-40 h-40 flex items-center justify-center">
                     
          </div>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleMSQSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fname" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="fname"
                      value={formData.fname}
                      onChange={(e) => handleInputChange('fname', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.fname ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your first name"
                    />
                    {errors.fname && (
                      <p className="mt-1 text-sm text-red-600">{errors.fname}</p>
                    )}
        </div>
                  
                  <div>
                    <label htmlFor="lname" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lname"
                      value={formData.lname}
                      onChange={(e) => handleInputChange('lname', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.lname ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your last name"
                    />
                    {errors.lname && (
                      <p className="mt-1 text-sm text-red-600">{errors.lname}</p>
                    )}
        </div>
      </div>

            <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
            </div>
            
            <div>
                  <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Your Email Address
                  </label>
                  <input
                    type="email"
                    id="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.confirmEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your email address"
                  />
                  {errors.confirmEmail && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmEmail}</p>
                  )}
            </div>
            
            <div>
                  <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Access Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="accessCode"
                      value={formData.accessCode}
                      onChange={(e) => handleInputChange('accessCode', e.target.value)}
                      className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.accessCode ? 'border-red-500 bg-red-50' : 
                        accessCodeValid ? 'border-green-500 bg-green-50' : 
                        accessCodeVerifying ? 'border-yellow-500 bg-yellow-50' : 
                        'border-gray-300'
                      }`}
                      placeholder="Enter your access code"
                    />
                    {(accessCodeVerifying || accessCodeValid || errors.accessCode) && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {accessCodeValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : accessCodeVerifying ? (
                          <div className="w-5 h-5 border-2 border-yellow-500 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          </div>
                        ) : errors.accessCode ? (
                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Validation Messages */}
                  {errors.accessCode && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.accessCode}
                    </p>
                  )}
                  
                  {accessCodeValid && !errors.accessCode && (
                    <p className="mt-1 text-sm text-green-600 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Valid access code
                    </p>
                  )}
                  
                  {accessCodeVerifying && (
                    <p className="mt-1 text-sm text-yellow-600 flex items-center">
                      <svg className="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying access code...
                    </p>
                  )}
            </div>
            
            {/* Submit Error Message */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.submit}
                </p>
              </div>
            )}
            
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-md font-medium hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{width: '100%'}}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      NEXT PAGE
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Welcome;
